// Novel interior planning projection V3A.
// This consumes the existing saved-source inventory and the V2 advisory print
// choices. It is not a production print profile or a physical pagination run.
// Textbook's proven source-unit startPolicy is "next_page" for both Chapter
// and Book Matter; forcing Chapter recto/right-hand pages is not adopted.
const clean = (value) => String(value ?? "").trim();
const safeArray = (value) => Array.isArray(value) ? value : [];
const safeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

export function buildNovelInteriorProductionPlanV3({
  sourceManifest = {}, printPlanning = {},
} = {}) {
  const source = safeObject(sourceManifest);
  const print = safeObject(printPlanning);
  const sections = safeArray(source.sections);
  const issues = safeArray(source.issues);

  const units = sections.map((section, position) => {
    const item = safeObject(section);
    const kind = ["front", "chapter", "back"].includes(clean(item.kind))
      ? clean(item.kind) : "unknown";
    return {
      position: position + 1,
      sourceId: clean(item.id),
      kind,
      title: clean(item.title),
      sceneCount: kind === "chapter" ? Math.max(0, Number(item.sceneCount) || 0) : 0,
      // The page-start rule is a proposal, not a measured or saved page break.
      startPolicy: "next_page",
      startPage: null,
      measuredPageCount: null,
    };
  });

  const frontMatterCount = units.filter((unit) => unit.kind === "front").length;
  const chapterCount = units.filter((unit) => unit.kind === "chapter").length;
  const backMatterCount = units.filter((unit) => unit.kind === "back").length;
  const sourceConsistent = source.readyForProductionContract === true &&
    issues.length === 0 && chapterCount > 0;
  const printRequested = print.printRequested === true;
  const digitalOnly = print.digitalOnly === true;
  const printChoicesCompatible = printRequested && print.settingsComplete === true;

  return {
    schemaVersion: "novel-interior-production-plan-v3a",
    status: "read_only_planning",
    units, sectionCount: units.length,
    frontMatterCount, chapterCount, backMatterCount,
    assignedCanonScenes: Math.max(0, Number(source.sceneCount) || 0),
    sourceIssues: issues.length, sourceConsistent,
    printRequested, digitalOnly, printChoicesCompatible,
    planningInputsCompatible: sourceConsistent && printChoicesCompatible,
    // Every item begins on a *new* page in the future measured interior.
    // Not "recto", not right-hand-only and not a measured physical page.
    sectionStartPolicy: "next_page",
    chapterSceneOrder: "saved_canon_order_within_chapter",
    forceRecto: false,
    designMarginAuthority: "pending_final_page_count_and_geometry",
    designMarginsInches: null,
    physicalPaginationMeasured: false,
    // A browser-measured private proof does not provide any of the below.
    verifiedProductionProfile: null,
    measuredInteriorPdf: null,
    finalPageCount: null,
    finalPageCountLocked: false,
    spineWidthInches: null,
    publishingEnabled: false,
    exportEnabled: false,
  };
}
